/**
 * SynastryChart — bi-roue de synastrie : le thème A (roue de base : zodiaque + maisons + planètes
 * sur un anneau intérieur) et le thème B (planètes sur un anneau extérieur), reliés par les lignes
 * d'aspects croisés A×B. Réutilise les renderers du thème astral ; seule la disposition en deux
 * anneaux et le tracé des aspects croisés (deux jeux de positions) sont propres à la synastrie.
 */
import { DEFAULT_OPTIONS, ASPECT_CONFIG } from './constants.js';
import { wheelAngle, adjustColorOpacity } from './utils.js';
import { DataProcessor } from '../data/DataProcessor.js';
import { CircleRenderer } from '../renderers/CircleRenderer.js';
import { ZodiacRenderer } from '../renderers/ZodiacRenderer.js';
import { HouseRenderer } from '../renderers/HouseRenderer.js';
import { PlanetRenderer } from '../renderers/PlanetRenderer.js';

const INNER_RING = 0.50; // anneau planètes thème A (% du rayon)
const OUTER_RING = 0.74; // anneau planètes thème B (% du rayon)

/**
 * LA TEINTE DE CHAQUE CÔTÉ, et c'est ce qui rend la bi-roue lisible.
 *
 * Sans elle, les deux jeux de planètes sont dessinés par le même renderer, avec les mêmes disques
 * colorés : rien ne distingue la Vénus de l'un de la Vénus de l'autre, et la roue redevient le joli
 * dessin dont on ne tire rien. Chaque disque porte donc un LISERÉ de la couleur de son propriétaire,
 * et chaque anneau son cercle de guidage — plein au-dedans, pointillé au-dehors, comme les pastilles
 * de la légende sous le dessin.
 *
 * Les valeurs recopient `--k-side-a` / `--k-side-b` de la feuille (components/_astro.scss) : aucune
 * règle CSS n'atteint un canevas, c'est la convention de tous les composants dessinés de ce thème
 * (cf. sky-chart/core/constants.js). Les changer d'un côté sans l'autre casse la correspondance
 * entre le dessin et sa légende.
 */
const SIDE_A_COLOR = '#af4d20'; // --k-fox-deep
const SIDE_B_COLOR = '#2f2842'; // --k-night

// Lisibilité : ne relier que les aspects croisés MAJEURS, et seulement les plus forts (les mineurs
// et les liens faibles noient la bi-roue). Le tableau, lui, liste tout.
const MAJOR_ASPECTS = new Set(['CONJUNCTION', 'OPPOSITION', 'TRINE', 'SQUARE', 'SEXTILE']);
const MAX_LINES = 12;

export class SynastryChart {
    /**
     * @param {HTMLCanvasElement} canvas
     * @param {Object} chartA - thème de la personne A (roue de base)
     * @param {Object} chartB - thème de la personne B (anneau extérieur)
     * @param {Array} crossAspects - aspects croisés {point1 (A), point2 (B), type, strength}
     */
    constructor(canvas, chartA, chartB, crossAspects) {
        this.canvas = canvas;
        this.ctx = canvas.getContext('2d');
        this.options = { ...DEFAULT_OPTIONS, size: Math.min(canvas.width, canvas.height) };
        this.circleRenderer = new CircleRenderer(this.ctx, this.options);
        this.zodiacRenderer = new ZodiacRenderer(this.ctx, this.options);
        this.houseRenderer = new HouseRenderer(this.ctx, this.options);
        this.planetRenderer = new PlanetRenderer(this.ctx, this.options);
        this.dataA = DataProcessor.prepareData(chartA);
        this.dataB = DataProcessor.prepareData(chartB);
        // A biwheel turns on the base chart's Ascendant — person A's, whose houses it draws.
        this.options.ascendant = this.dataA.houses?.[0]?.position ?? 0;
        this.crossAspects = crossAspects || [];
        this._adjustCanvasSize();
        this.draw();
        this._setupResizeHandler();
    }

    draw() {
        this._adjustCanvasSize();
        const ctx = this.ctx;
        const size = this.options.size;
        const center = size / 2;
        const radius = center - this.options.margin;
        // Canvas pas encore dimensionné (0px) → rayon négatif : on saute ce rendu, le ResizeObserver
        // redessinera dès que le canvas a une taille.
        if (radius <= 0) return;
        ctx.clearRect(0, 0, size, size);

        // Roue de base depuis le thème A : cercles, zodiaque, maisons. (Pas de décans : la bi-roue
        // porte déjà deux jeux de planètes + les aspects croisés, les décans surchargeraient.)
        this.circleRenderer.draw(center, center, radius);
        this.zodiacRenderer.drawZodiac(center, center, radius);
        this.houseRenderer.draw(center, center, radius, this.dataA.houses);

        // Deux anneaux de planètes à angle = longitude (A intérieur, B extérieur).
        const posA = this._ringPositions(center, this.dataA.planets, radius * INNER_RING);
        const posB = this._ringPositions(center, this.dataB.planets, radius * OUTER_RING);

        // Les cercles de guidage AVANT les traits : ils sont le fond sur lequel tout le reste se pose.
        this._drawRingGuide(center, radius * INNER_RING, SIDE_A_COLOR, false);
        this._drawRingGuide(center, radius * OUTER_RING, SIDE_B_COLOR, true);

        // Aspects croisés au centre (sous les planètes), puis les deux jeux de planètes par-dessus,
        // chacun posé sur une pastille de la couleur de son côté — c'est le liseré qui dépasse du
        // disque de la planète qui dit à qui elle appartient.
        this._drawCrossAspects(posA, posB);
        this._drawOwnerPads(radius, this.dataA.planets, posA, SIDE_A_COLOR);
        this.planetRenderer.draw(center, center, radius, this.dataA.planets, posA);
        this._drawOwnerPads(radius, this.dataB.planets, posB, SIDE_B_COLOR);
        this.planetRenderer.draw(center, center, radius, this.dataB.planets, posB);
    }

    /**
     * Le cercle sur lequel un anneau de planètes est posé — discret, il ne fait que rendre les DEUX
     * anneaux visibles en tant que tels. Plein pour celui du dedans, pointillé pour celui du dehors :
     * la légende sous le dessin reprend exactement ce couple (pastille pleine / pastille creuse).
     */
    _drawRingGuide(center, ringRadius, color, dashed) {
        const ctx = this.ctx;
        ctx.save();
        ctx.beginPath();
        ctx.arc(center, center, ringRadius, 0, Math.PI * 2);
        ctx.strokeStyle = adjustColorOpacity(color, 0.28);
        ctx.lineWidth = 1;
        ctx.setLineDash(dashed ? [4, 4] : []);
        ctx.stroke();
        ctx.restore();
    }

    /**
     * La pastille sous une planète : un disque de la couleur du côté, à peine plus large que celui
     * que le renderer va peindre par-dessus. Ce qui en reste visible est un liseré — assez pour
     * appartenir, trop peu pour rivaliser avec la couleur propre de l'astre.
     *
     * La taille recopie celle du renderer ({@code PlanetRenderer#draw}) : elle en est le seul point
     * de couplage, et une pastille trop petite disparaîtrait sous le disque plutôt que de fausser le
     * dessin — le mode de panne est bénin, à dessein.
     */
    _drawOwnerPads(radius, planets, positions, color) {
        const ctx = this.ctx;
        const symbolSize = Math.max(20, Math.round(radius * 0.07));
        const padSize = symbolSize * 0.7 + Math.max(2.5, radius * 0.007);
        ctx.save();
        ctx.fillStyle = color;
        (planets || []).forEach((planet) => {
            const at = positions[planet.name];
            if (!at) return;
            ctx.beginPath();
            ctx.arc(at.x, at.y, padSize, 0, Math.PI * 2);
            ctx.fill();
        });
        ctx.restore();
    }

    /** Positions des planètes sur un anneau de rayon fixe (angle = longitude écliptique). */
    _ringPositions(center, planets, ringRadius) {
        const positions = {};
        (planets || []).forEach((planet) => {
            const angle = wheelAngle(planet.position, this.options.ascendant);
            positions[planet.name] = {
                x: center + ringRadius * Math.cos(angle),
                y: center - ringRadius * Math.sin(angle),
                radius: ringRadius,
                angle,
                symbol: planet.symbol,
                retrograde: planet.retrograde,
            };
        });
        return positions;
    }

    /**
     * Lignes d'aspects croisés : point1 dans le thème A (posA), point2 dans le thème B (posB). Pour
     * rester lisible, on ne trace que les aspects MAJEURS et seulement les {@link MAX_LINES} plus forts,
     * en traits fins et discrets ; le tableau sous la roue reste, lui, exhaustif.
     */
    _drawCrossAspects(posA, posB) {
        const ctx = this.ctx;
        const strengthOf = (a) => Math.abs(a.strength ?? a.effectiveStrength ?? 0.5);
        const lines = this.crossAspects
            .filter((a) => MAJOR_ASPECTS.has(a.type) && posA[a.point1] && posB[a.point2])
            .sort((a, b) => strengthOf(b) - strengthOf(a))
            .slice(0, MAX_LINES);
        lines.forEach((aspect) => {
            const p1 = posA[aspect.point1];
            const p2 = posB[aspect.point2];
            const style = ASPECT_CONFIG[aspect.type] || { color: '#e6bfa4', width: 0.8, dash: [2, 2] };
            const strength = strengthOf(aspect);
            ctx.beginPath();
            ctx.moveTo(p1.x, p1.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.strokeStyle = adjustColorOpacity(style.color, strength * 0.4 + 0.14);
            ctx.lineWidth = style.width * (strength * 0.35 + 0.4);
            ctx.setLineDash(style.dash);
            ctx.stroke();
            ctx.setLineDash([]);
        });
    }

    _adjustCanvasSize() {
        const rect = this.canvas.getBoundingClientRect();
        const pixelRatio = window.devicePixelRatio || 1;
        const size = Math.min(rect.width, rect.height);
        this.canvas.width = size * pixelRatio;
        this.canvas.height = size * pixelRatio;
        this.canvas.style.width = `${size}px`;
        this.canvas.style.height = `${size}px`;
        this.ctx.scale(pixelRatio, pixelRatio);
        this.options.size = size;
    }

    _setupResizeHandler() {
        if (window.ResizeObserver) {
            const observer = new ResizeObserver(() => requestAnimationFrame(() => this.draw()));
            observer.observe(this.canvas);
        } else {
            window.addEventListener('resize', () => requestAnimationFrame(() => this.draw()));
        }
    }
}
